import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Chip,
  Divider,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { T } from '../admin/adminTheme';
import {
  AvailabilityChip,
  BoolChip,
  FilterableSortableTableHead,
  SectionCard,
  StyledTableRow,
  TabHeader,
  TableStateWrapper,
  applyColumnFilters,
  useSortableTable,
} from '../admin/shared';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ;

const COLUMNS = [
  { id: 'Employee_ID', label: 'Employee ID', minWidth: 110 },
  { id: 'EmployeeName', label: 'Employee Name', minWidth: 180 },
  { id: 'User_Category', label: 'Role', minWidth: 100 },
  { id: 'ActiveStatus', label: 'User Status', minWidth: 120 },
  { id: 'avaliability', label: 'Session', minWidth: 130 },
  { id: 'MachineGroup', label: 'Employee Group', minWidth: 140 },
  { id: 'Designation', label: 'Designation', minWidth: 130 },

];

export default function MasterUsersView({ supervisorId, onSnack }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState({});

  const load = useCallback(async () => {
    if (!supervisorId) return;

    setLoading(true);

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/machinesupervisor/master/users/${supervisorId}`
      );

      setList(Array.isArray(res.data) ? res.data : []);
      setColumnFilters({});
    } catch (err) {
      onSnack?.(
        err?.response?.data?.message || 'Failed to load allocated user accounts.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [onSnack, supervisorId]);

  useEffect(() => {
    load();
  }, [load]);

  const searchFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter((user) => (
      String(user.Employee_ID).toLowerCase().includes(q) ||
      user.EmployeeName?.toLowerCase().includes(q) ||
      user.User_Category?.toLowerCase().includes(q) ||
      user.MachineGroup?.toLowerCase().includes(q) ||
      user.Designation?.toLowerCase().includes(q) ||
      String(user.activeMachineCode || '').toLowerCase().includes(q)
    ));
  }, [list, search]);

  const columnFilteredRows = useMemo(
    () => applyColumnFilters(searchFiltered, columnFilters),
    [searchFiltered, columnFilters]
  );

  const {
    sortedRows: rows,
    sortField,
    sortDir,
    handleSort,
  } = useSortableTable(columnFilteredRows, 'Employee_ID');

  return (
    <Fade in>
      <Box>
        <SectionCard>
          <TabHeader
            count={rows.length}
            noun="Allocated User Accounts"
            search={search}
            onSearch={setSearch}
            onRefresh={load}
            hideAdd
          />

          <Divider />

          <TableStateWrapper
            loading={loading}
            empty={rows.length === 0}
            emptyMessage={search ? 'No allocated user accounts match your search.' : 'No allocated user accounts found.'}
            colSpan={COLUMNS.length}
            minHeight={360}
            skeletonRows={7}
          >
            <TableContainer>
              <Table>
                <FilterableSortableTableHead
                  columns={COLUMNS}
                  sourceRows={searchFiltered}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={setColumnFilters}
                />

                <TableBody>
                  {rows.map((user) => (
                    <StyledTableRow key={user.Employee_ID}>
                      <TableCell sx={{ fontWeight: 800, fontFamily: 'monospace', color: t.heading }}>
                        {user.Employee_ID}
                      </TableCell>

                      <TableCell>{user.EmployeeName || '—'}</TableCell>

                      <TableCell>
                        <Chip size="small" label={user.User_Category || '—'} variant="outlined" />
                      </TableCell>

                      <TableCell>
                        <BoolChip value={user.ActiveStatus} />
                      </TableCell>

                      <TableCell>
                        <AvailabilityChip value={user.avaliability} />
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: t.primary }}>
                          {user.MachineGroup || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>{user.Designation || '—'}</TableCell>

                    
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TableStateWrapper>
        </SectionCard>
      </Box>
    </Fade>
  );
}
